import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  MapPinIcon,
  ChevronLeftIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

import { savedAPI, createQueryKey } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import ProjectCard from '../components/ProjectCard';
import SavedListModal from '../components/SavedListModal';

const SavedDetailPage = () => {
  const { user } = useAuth();
  const { listId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Modal state
  const [listModal, setListModal] = useState({ isOpen: false, list: null, mode: 'edit' });
  
  // Fetch the specific list with its items
  const { 
    data: selectedListData,
    isLoading,
    error,
    refetch 
  } = useQuery({
    queryKey: createQueryKey.savedList(listId),
    queryFn: () => savedAPI.getList(listId, true),
    enabled: !!listId && !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes - prevents refetching during animations
    cacheTime: 10 * 60 * 1000, // 10 minutes - keeps data in cache
    keepPreviousData: true, // Keep previous data during transitions
    retry: (failureCount, error) => {
      // Don't retry if the list was deleted (404 error)
      if (error?.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
    onError: (error) => {
      // If list not found (404), go back to lists view
      if (error?.response?.status === 404) {
        navigate('/saved');
        toast.error('List not found. It may have been deleted.');
      }
    }
  });

  const selectedList = selectedListData?.data?.data?.list;
  const projects = selectedList?.items?.map(item => item.project) || [];


  const handleBackToLists = () => {
    navigate('/profile', {
      state: {
        previousPath: location.pathname,
        transitionDirection: 'back'
      }
    });
  };

  // Handle list actions
  const handleEditList = (list) => {
    setListModal({ isOpen: true, list, mode: 'edit' });
  };

  const closeListModal = () => {
    setListModal({ isOpen: false, list: null, mode: 'edit' });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {/* Back Button */}
              <button
                onClick={handleBackToLists}
                className="flex items-center p-3 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
              
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white truncate" title={selectedList?.name}>
                {selectedList?.name || 'List'}
              </h1>
            </div>

            {/* Edit Button */}
            {selectedList && (
              <button
                onClick={() => handleEditList(selectedList)}
                className="p-3 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
                title="Edit list"
              >
                <PencilIcon className="w-5 h-5" />
              </button>
            )}

          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-2">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden animate-pulse">
                  <div className="w-full h-48 bg-gray-300 dark:bg-gray-600"></div>
                  <div className="p-4">
                    <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded mb-2"></div>
                    <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-600 dark:text-red-400 mb-4">
                Failed to load saved list
                <br />
                <small className="text-sm">Error: {error.message}</small>
                {error.response?.status === 401 && (
                  <div className="mt-2 text-sm">
                    <strong>Please log in to view your saved lists</strong>
                  </div>
                )}
              </div>
              <button
                onClick={() => refetch()}
                className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : (
            /* Projects View */
            projects.length === 0 ? (
              <div className="text-center py-12">
                <MapPinIcon className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">Nothing in this list</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Start adding things from the explore page to build your collection.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {projects.map((project) => (
                  <ProjectCard 
                    key={project.id} 
                    project={project}
                    referrer={`/saved/${listId}`}
                  />
                ))}
              </div>
            )
          )}
        </div>
      </div>

      {/* List Modal */}
      <SavedListModal
        isOpen={listModal.isOpen}
        onClose={closeListModal}
        list={listModal.list}
        mode={listModal.mode}
      />
    </div>
  );
};

export default SavedDetailPage;
